import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Checkbox } from 'primereact/checkbox';

// Servicios
import { obtenerConfiguracionDet } from '../../services/Configuracion/ConfiguracionDetService'; 
import { 
    getCodigoDet, 
    creaNuevoDet,
    getAllConfigTipo, 
    getConfigCabByTipo 
} from '../../services/Roles/RolesService';

export default function AddConfigModal({ 
    visible, 
    onHide, 
    configCabCode, 
    onSuccess 
}) {
    const [nombre, setNombre] = useState('');
    const [parametro, setParametro] = useState('');
    const [codigoGenerado, setCodigoGenerado] = useState('');
    const [activo, setActivo] = useState(true);
    
    // Estados de UI
    const [loading, setLoading] = useState(false);
    const [statusMsg, setStatusMsg] = useState(''); 
    const [cabeceraId, setCabeceraId] = useState(null); 
    const toast = useRef(null);

    useEffect(() => {
        if (visible && configCabCode) {
            inicializarDatos();
        } else {
            limpiarFormulario();
        }
    }, [visible, configCabCode]);

    const limpiarFormulario = () => {
        setNombre('');
        setParametro('');
        setCodigoGenerado('');
        setStatusMsg('');
        setActivo(true);
        setCabeceraId(null);
    };

    // --- LÓGICA DE BÚSQUEDA PROFUNDA ---
    // Busca el ID numérico de la cabecera (ej: NOM-0000-0013) iterando los Tipos
    const buscarIdCabeceraRemoto = async (codigoBuscado) => {
        try {
            // 1. Traemos todos los TIPOS de configuración
            const tipos = await getAllConfigTipo();
            if (!tipos || tipos.length === 0) return null;

            // 2. Buscamos en paralelo las CABECERAS de esos tipos
            const promesasCabeceras = tipos.map(t => 
                getConfigCabByTipo(t.configuracionTipoId)
                    .then(res => (Array.isArray(res) ? res : [])) // Aseguramos que sea array
                    .catch(() => [])
            );
            
            const resultados = await Promise.all(promesasCabeceras);
            
            // 3. Aplanamos todos los arrays y buscamos el código
            const todasLasCabeceras = resultados.flat();
            const cabeceraEncontrada = todasLasCabeceras.find(c => c.codigo === codigoBuscado);

            return cabeceraEncontrada ? cabeceraEncontrada.configuracionCabId : null;

        } catch (error) {
            console.error("Error en búsqueda profunda:", error);
            return null;
        }
    };

    const inicializarDatos = async () => {
        setLoading(true);
        setStatusMsg('Verificando configuración...');
        setCodigoGenerado('');

        try {
            // INTENTO 1: Sacar ID de la lista de hijos (Rápido)
            const respuesta = await obtenerConfiguracionDet(configCabCode);
            // Normalizar respuesta (data vs data.data vs array directo)
            let listaActual = [];
            if (Array.isArray(respuesta)) listaActual = respuesta;
            else if (respuesta?.data && Array.isArray(respuesta.data)) listaActual = respuesta.data;
            
            let idPadre = null;

            // Si hay hijos y tienen el ID del padre, lo usamos
            if (listaActual.length > 0 && listaActual[0].configuracionCabId) {
                idPadre = listaActual[0].configuracionCabId;
            } 
            
            // INTENTO 2: Búsqueda Profunda (Si la lista está vacía o el backend no mandó IDs)
            if (!idPadre) {
                setStatusMsg('Escaneando cabeceras...');
                idPadre = await buscarIdCabeceraRemoto(configCabCode);
            }

            if (idPadre) {
                setCabeceraId(idPadre);
                setStatusMsg('Generando código...');
                
                // 3. Generar Código
                const nuevoCodigo = await getCodigoDet(idPadre, configCabCode);
                if (nuevoCodigo) {
                    setCodigoGenerado(nuevoCodigo);
                    setStatusMsg(''); 
                } else {
                    setStatusMsg('Error: El servicio no devolvió un código.');
                }
            } else {
                setStatusMsg('Error: No se encontró la Cabecera ' + configCabCode);
                toast.current.show({ severity: 'error', summary: 'Error Fatal', detail: `No existe la cabecera ${configCabCode} en la BD` });
            }

        } catch (error) {
            console.error("Error modal:", error);
            setStatusMsg('Error de conexión.');
        } finally {
            setLoading(false);
        }
    };

    const handleGuardar = async () => {
        if (!nombre.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese un nombre' });
            return;
        }
        if (!cabeceraId || !codigoGenerado) {
            toast.current.show({ severity: 'error', summary: 'Bloqueo', detail: 'No se puede guardar sin Código generado' });
            return;
        }

        setLoading(true);
        try {
            const payload = {
                padreConfiguracionDetId: 0,
                configuracionCabId: cabeceraId,
                codigo: codigoGenerado,
                nombre: nombre.toUpperCase(),
                parametro: parametro || "1",
                estado: 'Activo',
                activo: activo
            };

            await creaNuevoDet(payload);
            
            toast.current.show({ severity: 'success', summary: 'Guardado', detail: 'Registro agregado correctamente' });
            
            if (onSuccess) onSuccess(configCabCode);
            setTimeout(() => onHide(), 500);

        } catch (error) {
            console.error(error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog header={`Agregar a: ${configCabCode}`} visible={visible} style={{ width: '450px' }} onHide={onHide} modal className="p-fluid">
            <Toast ref={toast} />
            
            <div className="formgrid grid">
                <div className="field col-12">
                    <label>Código</label>
                    <div className="p-inputgroup">
                        <span className="p-inputgroup-addon">
                            <i className={loading ? "pi pi-spin pi-spinner" : "pi pi-key"}></i>
                        </span>
                        <InputText 
                            value={codigoGenerado} 
                            readOnly 
                            className={statusMsg ? "p-invalid" : "surface-200 font-bold"} 
                            placeholder={statusMsg || "Generando..."} 
                        />
                    </div>
                    {statusMsg && <small className="text-primary block mt-1"><i className="pi pi-info-circle mr-1"></i>{statusMsg}</small>}
                </div>

                <div className="field col-12">
                    <label>Nombre <span className="text-red-500">*</span></label>
                    <InputText value={nombre} onChange={(e) => setNombre(e.target.value.toUpperCase())} autoFocus />
                </div>

                <div className="field col-12">
                    <label>Parametro</label>
                    <InputTextarea value={parametro} onChange={(e) => setParametro(e.target.value)} rows={2} />
                </div>

                <div className="field col-12 flex align-items-center gap-2">
                    <Checkbox inputId="chkActivo" checked={activo} onChange={(e) => setActivo(e.checked)} />
                    <label htmlFor="chkActivo" className="cursor-pointer">Activo</label>
                </div>

                <div className="col-12 flex justify-content-end gap-2 mt-2">
                    <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={onHide} />
                    <Button label="Guardar" icon="pi pi-check" loading={loading} onClick={handleGuardar} disabled={!codigoGenerado} />
                </div>
            </div>
        </Dialog>
    );
}